import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Chip,
  Avatar,
  Alert,
  IconButton,
  Collapse,
  Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";

import AppButton from "../../ui/AppButton";
import AppSelect from "../../ui/AppSelect";
import AppDatePicker from "../../ui/AppDatePicker";
import DataTable from "../../ui/Table";
import RichTextEditor from "../../ui/RichTextEditor";
import { getTaskComments, createTaskComment } from "../../services/taskService";
import CommentThread from "../common/CommentThread";

const TASK_TYPES = ["Task", "Bug", "Story"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const STATUSES = ["To Do", "In Progress", "Done"];

const TASK_TYPE_ICON = {
  Task: <TaskAltOutlinedIcon fontSize="small" sx={{ color: "#2563eb" }} />,
  Bug: <BugReportOutlinedIcon fontSize="small" sx={{ color: "#dc2626" }} />,
  Story: <AutoStoriesOutlinedIcon fontSize="small" sx={{ color: "#16a34a" }} />,
};

const PRIORITY_ICON = {
  Low: <KeyboardDoubleArrowDownIcon fontSize="small" sx={{ color: "#16a34a" }} />,
  Medium: <DragHandleIcon fontSize="small" sx={{ color: "#ca8a04" }} />,
  High: <KeyboardDoubleArrowUpIcon fontSize="small" sx={{ color: "#ea580c" }} />,
  Critical: <PriorityHighIcon fontSize="small" sx={{ color: "#dc2626" }} />,
};

const TASK_TYPE_OPTIONS = TASK_TYPES.map((type) => ({
  value: type,
  label: (
    <Box display="flex" alignItems="center" gap={1}>
      {TASK_TYPE_ICON[type]}
      {type}
    </Box>
  )
}));

const PRIORITY_OPTIONS = PRIORITIES.map((priority) => ({
  value: priority,
  label: (
    <Box display="flex" alignItems="center" gap={1}>
      {PRIORITY_ICON[priority]}
      {priority}
    </Box>
  )
}));

const STATUS_OPTIONS = STATUSES.map((status) => ({ value: status, label: status }));

const priorityStyle = (priority) => {
  switch (priority) {
    case "Critical":
      return { background: "#fee2e2", color: "#dc2626" };
    case "High":
      return { background: "#ffedd5", color: "#ea580c" };
    case "Medium":
      return { background: "#fef3c7", color: "#ca8a04" };
    case "Low":
      return { background: "#dcfce7", color: "#16a34a" };
    default:
      return { background: "#f1f5f9", color: "#475569" };
  }
};

const statusAccent = (status) => {
  switch (status) {
    case "To Do":
      return "#94a3b8";
    case "In Progress":
      return "#7c3aed";
    case "Done":
      return "#16a34a";
    default:
      return "#94a3b8";
  }
};

function TaskDialog({
  open,
  task,
  projectTeam = [],
  sprints = [],
  allTasks = [],
  defaultSprintId = null,
  isSaving = false,
  currentEmployeeName = "",
  currentEmployeeId = null,
  fullScreen = false,
  onClose,
  onSave,
  onCreateSubtask,
  onEditSubtask,
}) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    task_type: "Task",
    priority: "Medium",
    status: "To Do",
    story_points: "",
    assigned_to: "",
    sprint_id: "",
    created_by: "",
    start_date: "",
    due_date: "",
  });
  const [error, setError] = useState("");
  const [subtaskTitle, setSubtaskTitle] = useState("");
  const [isAddingSubtask, setIsAddingSubtask] = useState(false);
  const [moreFieldsOpen, setMoreFieldsOpen] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      return;
    }

    if (task) {
      setForm({
        title: task.title || "",
        description: task.description || "",
        task_type: task.task_type || "Task",
        priority: task.priority || "Medium",
        status: task.status || "To Do",
        story_points: task.story_points ?? "",
        assigned_to: task.assigned_to ?? "",
        sprint_id: task.sprint_id ?? "",
        created_by: task.created_by ?? "",
        start_date: task.start_date ? task.start_date.slice(0, 10) : "",
        due_date: task.due_date ? task.due_date.slice(0, 10) : "",
      });
    } else {
      setForm({
        title: "",
        description: "",
        task_type: "Task",
        priority: "Medium",
        status: "To Do",
        story_points: "",
        assigned_to: "",
        sprint_id: defaultSprintId ?? "",
        created_by: currentEmployeeId ?? "",
        start_date: "",
        due_date: "",
      });
    }
    setError("");
    setSubtaskTitle("");
    setIsAddingSubtask(false);
  }, [open, task, defaultSprintId, currentEmployeeId]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const subtasks = task ? allTasks.filter((t) => t.parent_task_id === task.task_id) : [];

  const handleAddSubtask = async () => {
    if (!subtaskTitle.trim() || isAddingSubtask) {
      return;
    }
    setIsAddingSubtask(true);
    try {
      await onCreateSubtask(task, subtaskTitle.trim());
      setSubtaskTitle("");
    } finally {
      setIsAddingSubtask(false);
    }
  };

  const handleSave = () => {
    if (!form.title.trim()) {
      setError("Task title is required");
      return;
    }

    onSave({
      title: form.title.trim(),
      description: form.description || null,
      task_type: form.task_type,
      status: form.status,
      priority: form.priority,
      story_points: form.story_points === "" ? null : Number(form.story_points),
      assigned_to: form.assigned_to === "" ? null : Number(form.assigned_to),
      sprint_id: form.sprint_id === "" ? null : Number(form.sprint_id),
      created_by: form.created_by === "" ? null : Number(form.created_by),
      start_date: form.start_date || null,
      due_date: form.due_date || null,
    });
  };

  const handleCopyLink = () => {
    if (!task) return;
    const link = `${window.location.origin}/board/${task.project_id}/task/${task.task_id}`;
    navigator.clipboard.writeText(link).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    });
  };

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        sx: fullScreen
          ? { width: "100vw", height: "100vh", m: 0, borderRadius: 0 }
          : { width: "90vw", height: "90vh", m: 0 },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {task && (
            <>
              <Tooltip title="Open task details">
                <Chip
                  label={`#${task.task_id}`}
                  size="small"
                  onClick={() => navigate(`/board/${task.project_id}/task/${task.task_id}`)}
                  sx={{ cursor: "pointer", fontWeight: 700, bgcolor: "background.default", border: "1px solid", borderColor: "divider" }}
                />
              </Tooltip>
              <Tooltip title={linkCopied ? "Link copied!" : "Copy link"}>
                <IconButton size="small" onClick={handleCopyLink}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
          {task ? "Update Task" : "Create Task"}
        </Box>

        <IconButton onClick={onClose} disabled={isSaving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: "background.default", p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 3, mb: 0 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 4fr) minmax(0, 1fr)" },
            gap: 3,
            p: { xs: 2.5, md: 3 },
            height: "100%",
          }}
        >
          {/* Left column: Title + Description */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
            {task?.parent_task_id && (
              <Chip
                icon={<SubdirectoryArrowRightIcon fontSize="small" />}
                label={`Subtask of "${task.parent_task_title || "task"}"`}
                size="small"
                sx={{ alignSelf: "flex-start", bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}
              />
            )}

            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                Title
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter task title"
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
              />
            </Box>

            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                Description
              </Typography>

              <RichTextEditor
                className="task-description-editor"
                value={form.description}
                onChange={(value) => setField("description", value)}
                placeholder="Enter task description"
              />
            </Box>

            {task && !task.parent_task_id && (
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                  Subtasks {subtasks.length > 0 && `(${subtasks.filter((s) => s.status === "Done").length}/${subtasks.length})`}
                </Typography>

                {subtasks.length > 0 && (
                  <Box sx={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", bgcolor: "background.default", mb: 1.5 }}>
                    <Box sx={{ width: `${(subtasks.filter((s) => s.status === "Done").length / subtasks.length) * 100}%`, bgcolor: "#16a34a" }} />
                    <Box sx={{ width: `${(subtasks.filter((s) => s.status !== "Done").length / subtasks.length) * 100}%`, bgcolor: "#2563eb" }} />
                  </Box>
                )}

                {subtasks.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <DataTable
                      columns={[
                        {
                          key: "title",
                          label: "Title",
                          render: (subtask) => (
                            <Typography sx={{ fontSize: 13, color: "text.primary" }} noWrap>
                              {subtask.title}
                            </Typography>
                          )
                        },
                        {
                          key: "priority",
                          label: "Priority",
                          render: (subtask) => (
                            <Chip
                              icon={PRIORITY_ICON[subtask.priority]}
                              label={subtask.priority}
                              size="small"
                              sx={{ height: 20, fontSize: 10, fontWeight: 600, ...priorityStyle(subtask.priority) }}
                            />
                          )
                        },
                        {
                          key: "status",
                          label: "Status",
                          render: (subtask) => (
                            <Chip
                              icon={<Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: statusAccent(subtask.status) }} />}
                              label={subtask.status}
                              size="small"
                              sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: "background.default", border: "1px solid", borderColor: "divider" }}
                            />
                          )
                        },
                        {
                          key: "assigned_name",
                          label: "Assignee",
                          render: (subtask) => (
                            <Box display="flex" alignItems="center" gap={0.8}>
                              <Avatar sx={{ width: 22, height: 22, fontSize: 10 }}>
                                {(subtask.assigned_name || "?").slice(0, 1).toUpperCase()}
                              </Avatar>
                              <Typography sx={{ fontSize: 12, color: "text.secondary" }} noWrap>
                                {subtask.assigned_name || "Unassigned"}
                              </Typography>
                            </Box>
                          )
                        }
                      ]}
                      rows={subtasks}
                      rowKey={(subtask) => subtask.task_id}
                      onRowClick={onEditSubtask}
                      pagination={false}
                    />
                  </Box>
                )}

                <Box display="flex" gap={1}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add a subtask"
                    value={subtaskTitle}
                    disabled={isAddingSubtask}
                    onChange={(e) => setSubtaskTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddSubtask();
                      }
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
                  />
                  <AppButton
                    variant="outlined"
                    startIcon={<AddIcon fontSize="small" />}
                    loading={isAddingSubtask}
                    onClick={handleAddSubtask}
                    disabled={!subtaskTitle.trim()}
                    sx={{ height: "auto", flexShrink: 0 }}
                  >
                    {isAddingSubtask ? "Adding..." : "Add"}
                  </AppButton>
                </Box>
              </Box>
            )}

            {task && (
              <CommentThread
                key={`task-${task.task_id}`}
                entityId={task.task_id}
                members={projectTeam}
                currentEmployeeId={currentEmployeeId}
                currentEmployeeName={currentEmployeeName}
                fetchComments={getTaskComments}
                postComment={createTaskComment}
              />
            )}
          </Box>

          {/* Right column: everything else */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
            <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
              <Box
                onClick={() => setDetailsOpen((prev) => !prev)}
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.4, cursor: "pointer" }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Details
                </Typography>
                <IconButton size="small">
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ transform: detailsOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}
                  />
                </IconButton>
              </Box>

              <Collapse in={detailsOpen}>
                <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Type
                    </Typography>
                    <AppSelect
                      options={TASK_TYPE_OPTIONS}
                      value={form.task_type}
                      onChange={(value) => setField("task_type", value)}
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Priority
                    </Typography>
                    <AppSelect
                      options={PRIORITY_OPTIONS}
                      value={form.priority}
                      onChange={(value) => setField("priority", value)}
                    />
                  </Box>

                  {task && (
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                        Status
                      </Typography>
                      <AppSelect
                        options={STATUS_OPTIONS}
                        value={form.status}
                        onChange={(value) => setField("status", value)}
                      />
                    </Box>
                  )}

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Report To
                    </Typography>
                    <AppSelect
                      options={[
                        ...(!projectTeam.some((member) => member.employee_id === form.created_by) && form.created_by !== ""
                          ? [{ value: form.created_by, label: task?.created_by_name || currentEmployeeName || "Unknown" }]
                          : []),
                        ...projectTeam.map((member) => ({ value: member.employee_id, label: member.employee_name }))
                      ]}
                      value={form.created_by}
                      onChange={(value) => setField("created_by", value)}
                      withAvatar
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Assignee To
                    </Typography>
                    <AppSelect
                      options={[
                        { value: "", label: "Unassigned" },
                        ...projectTeam.map((member) => ({ value: member.employee_id, label: member.employee_name }))
                      ]}
                      value={form.assigned_to}
                      onChange={(value) => setField("assigned_to", value)}
                      withAvatar
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Start Date
                    </Typography>
                    <AppDatePicker
                      selected={form.start_date ? new Date(form.start_date) : null}
                      onChange={(date) => setField("start_date", date ? date.toISOString().split("T")[0] : "")}
                      placeholder="Select start date"
                      popperPlacement="bottom-start"
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Due Date
                    </Typography>
                    <AppDatePicker
                      selected={form.due_date ? new Date(form.due_date) : null}
                      onChange={(date) => setField("due_date", date ? date.toISOString().split("T")[0] : "")}
                      minDate={form.start_date ? new Date(form.start_date) : null}
                      placeholder="Select due date"
                      popperPlacement="bottom-end"
                    />
                  </Box>
                </Box>
              </Collapse>
            </Box>

            <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
              <Box
                onClick={() => setMoreFieldsOpen((prev) => !prev)}
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.4, cursor: "pointer" }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  More Fields
                </Typography>
                <IconButton size="small">
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ transform: moreFieldsOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}
                  />
                </IconButton>
              </Box>

              <Collapse in={moreFieldsOpen}>
                <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Story Points
                    </Typography>
                    <TextField
                      type="number"
                      fullWidth
                      size="small"
                      placeholder="e.g. 3"
                      value={form.story_points}
                      onChange={(e) => setField("story_points", e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.default" } }}
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Sprint
                    </Typography>
                    <AppSelect
                      options={[
                        { value: "", label: "Backlog" },
                        ...sprints.map((sprint) => ({ value: sprint.sprint_id, label: sprint.sprint_name }))
                      ]}
                      value={form.sprint_id}
                      onChange={(value) => setField("sprint_id", value)}
                    />
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </Box>
      </DialogContent>


      <DialogActions sx={{ px: 3, py: 2, bgcolor: "background.paper", borderTop: "1px solid", borderColor: "divider" }}>
        <AppButton variant="text" onClick={onClose} disabled={isSaving}>
          Cancel
        </AppButton>

        <AppButton
          variant="contained"
          onClick={handleSave}
          loading={isSaving}
        >
          {isSaving ? "Saving..." : "Save"}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}

export default TaskDialog;
